
export type BorderAppearance = 'standard' | 'reverse' | 'filled'

export interface MapAppearance {
  borderAppearance: BorderAppearance
  pinBackground: boolean
  labelBackground: boolean
  pinNamesVisible: boolean
  /**
   * Layer visibility: whether each layer is drawn on the map surfaces and
   * included in the printed PDF.
   *
   * These are the app's view-settings switches (`areForbiddenAreasVisible`,
   * `areSubareasVisible`, `areArrowsVisible`, `areLinesVisible`), so web offers
   * the same set. Forbidden areas were once kept unconditional here on the
   * reasoning that a no-hunting zone should never be hidden from a map taken
   * into the field, but the app has always let a hunter hide them, and a web
   * map that cannot do what the phone does reads as a missing feature rather
   * than a safety rule. Every one defaults to on, as the app's wire mapper
   * reads an absent field.
   *
   * The hunting-ground border itself has no switch on either platform: it is
   * what the map is of.
   */
  forbiddenAreasVisible: boolean
  subAreasVisible: boolean
  arrowsVisible: boolean
  /** Hand-drawn lines. A recorded GPS trail is not a line for this switch. */
  linesVisible: boolean
  /**
   * CLUB-667: pin types the read-only surfaces do not draw, by
   * `MapObject['type']`.
   *
   * A *hidden* set rather than a visible one, for two reasons: a pin type added
   * in a later release defaults to visible for an existing user, and
   * `MapObject['type']` is far wider than the ids the Remote Config pin-type
   * catalog lists, so a visible-set model would silently erase every unlisted
   * type from the map.
   *
   * `string[]`, not a `Set`: this round-trips through `JSON.stringify` in
   * `useLocalStorage`, and a Set serialises to `{}`.
   */
  hiddenPinTypes: string[]
  pinScale: number       // 0.5–2.0
  pinOpacity: number     // 25–100
  labelOpacity: number   // 25–100
  lineThickness: {
    border: number       // 0.5–2.0
    subarea: number
    forbidden: number
    lines: number
    arrows: number
  }
}

export const MAP_APPEARANCE_STORAGE_KEY = 'wehunt:mapAppearance:v1'

export const DEFAULT_MAP_APPEARANCE: MapAppearance = {
  borderAppearance: 'standard',
  pinBackground: true,
  labelBackground: true,
  pinNamesVisible: true,
  forbiddenAreasVisible: true,
  subAreasVisible: true,
  arrowsVisible: true,
  linesVisible: true,
  hiddenPinTypes: [],
  pinScale: 1.0,
  pinOpacity: 100,
  labelOpacity: 100,
  lineThickness: {
    border: 1.0,
    subarea: 1.0,
    forbidden: 1.0,
    lines: 1.0,
    arrows: 1.0,
  },
}

const BORDER_VALUES: readonly BorderAppearance[] = ['standard', 'reverse', 'filled']

function clamp(v: unknown, lo: number, hi: number, fallback: number): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : fallback
  return Math.min(hi, Math.max(lo, n))
}

function clampBool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback
}

/**
 * De-duplicates the hidden pin-type ids and, when the catalog is known, drops
 * ids it does not list — an id with no switch anywhere could otherwise never
 * be shown again and would inflate the "N hidden" count. Without a predicate
 * (catalog not fetched yet) every id is kept: filtering against an empty
 * catalog would wipe the user's list on a slow first load.
 */
function clampHiddenPinTypes(v: unknown, isKnownPinType?: (id: string) => boolean): string[] {
  if (!Array.isArray(v)) return []
  const seen = new Set<string>()
  for (const entry of v) {
    if (typeof entry !== 'string') continue
    if (isKnownPinType && !isKnownPinType(entry)) continue
    seen.add(entry)
  }
  return [...seen]
}

export function clampAppearance(
  input: Partial<MapAppearance> | undefined | null,
  isKnownPinType?: (id: string) => boolean,
): MapAppearance {
  const i: Partial<MapAppearance> = input ?? {}
  const lt: Partial<MapAppearance['lineThickness']> = i.lineThickness ?? {}
  const border =
    typeof i.borderAppearance === 'string' &&
    BORDER_VALUES.includes(i.borderAppearance as BorderAppearance)
      ? (i.borderAppearance as BorderAppearance)
      : DEFAULT_MAP_APPEARANCE.borderAppearance
  return {
    borderAppearance: border,
    pinBackground: clampBool(i.pinBackground, DEFAULT_MAP_APPEARANCE.pinBackground),
    labelBackground: clampBool(i.labelBackground, DEFAULT_MAP_APPEARANCE.labelBackground),
    pinNamesVisible: clampBool(i.pinNamesVisible, DEFAULT_MAP_APPEARANCE.pinNamesVisible),
    forbiddenAreasVisible: clampBool(i.forbiddenAreasVisible, DEFAULT_MAP_APPEARANCE.forbiddenAreasVisible),
    subAreasVisible: clampBool(i.subAreasVisible, DEFAULT_MAP_APPEARANCE.subAreasVisible),
    arrowsVisible: clampBool(i.arrowsVisible, DEFAULT_MAP_APPEARANCE.arrowsVisible),
    linesVisible: clampBool(i.linesVisible, DEFAULT_MAP_APPEARANCE.linesVisible),
    hiddenPinTypes: clampHiddenPinTypes(i.hiddenPinTypes, isKnownPinType),
    pinScale: clamp(i.pinScale, 0.5, 2.0, DEFAULT_MAP_APPEARANCE.pinScale),
    pinOpacity: clamp(i.pinOpacity, 25, 100, DEFAULT_MAP_APPEARANCE.pinOpacity),
    labelOpacity: clamp(i.labelOpacity, 25, 100, DEFAULT_MAP_APPEARANCE.labelOpacity),
    lineThickness: {
      border: clamp(lt.border, 0.5, 2.0, DEFAULT_MAP_APPEARANCE.lineThickness.border),
      subarea: clamp(lt.subarea, 0.5, 2.0, DEFAULT_MAP_APPEARANCE.lineThickness.subarea),
      forbidden: clamp(lt.forbidden, 0.5, 2.0, DEFAULT_MAP_APPEARANCE.lineThickness.forbidden),
      lines: clamp(lt.lines, 0.5, 2.0, DEFAULT_MAP_APPEARANCE.lineThickness.lines),
      arrows: clamp(lt.arrows, 0.5, 2.0, DEFAULT_MAP_APPEARANCE.lineThickness.arrows),
    },
  }
}

/**
 * Whether a drawn line of this kind is shown under the layer switches.
 *
 * The app's rule (`visibleMapLines` in drive's `map_lines_layer.dart`): an
 * arrow follows the arrows switch, a trail follows the trails switch, and every
 * other kind follows the lines switch. Web has no trails switch, so a trail is
 * always drawn — it must not disappear because the user turned off LINES, a
 * layer the app would never have hidden it with.
 */
export function isLineKindVisible(
  appearance: Pick<MapAppearance, 'arrowsVisible' | 'linesVisible'>,
  kind: string,
): boolean {
  if (kind === 'trail') return true
  return kind === 'arrow' ? appearance.arrowsVisible : appearance.linesVisible
}

/**
 * Whether a border/sub-area/forbidden polygon can be selected by a click.
 *
 * Selection is blocked ONLY while the user is actively drawing a shape,
 * editing a polygon's vertices, or placing a pin. It is deliberately NOT
 * gated on which layer tab is active: clicking any polygon selects it and the
 * parent `onPolygonSelect` switches to that polygon's own tab. Mirrors how a
 * border is tapped on mobile (a point hit-test on the hunting-ground map,
 * independent of any mode).
 *
 * Borders used to add an `activeTab === "border"` gate that the sub-area and
 * forbidden layers never had, so a border was unselectable from any other tab
 * — the reported "property borders cannot be selected" bug. All three layers
 * now share this one rule.
 */
export function isPolygonSelectable(opts: {
  drawing: boolean;
  vertexEditing: boolean;
  placeMode: boolean;
}): boolean {
  return !opts.drawing && !opts.vertexEditing && !opts.placeMode;
}

/**
 * Colours for a pin's name label on the map.
 *
 * NOTHING here follows the pin's colour — not the text, not the outline. The
 * label's whole job is to stay readable over an arbitrary map tile, and the pin
 * itself already carries the colour. Tinting the label made a recoloured pin
 * read as a second competing shape floating above it, and a pin in a dark colour
 * lost its label against the map entirely.
 *
 * This deliberately departs from Figma 19165:25840 / 19165:25872, which tinted
 * the label text to match the pin. Changed on Jenny's call at design review
 * (2026-08-17) after seeing it on a real map.
 *
 * That is why the pin is not a parameter: with no colour in scope, the label
 * *cannot* be wired to it. The previous version took the pin and reached for
 * `pin.color`, which is how the tint crept into the outline in the first place.
 *
 * The outline is two different things across the two modes, so they are separate
 * fields rather than one "outline": with label backgrounds on it is the pill's
 * border, pure white so it holds against the translucent black pill in either
 * theme; with them off it is a white halo around dark text.
 *
 * None of these follow the colour theme. The label sits on the map tiles, and the
 * tiles stay light in dark mode, so theme tokens there flipped the label to light
 * text in a dark halo — a hollow outline that was hard to read on a light map.
 * The label therefore looks the same in both themes.
 */
/** The light theme's `--nl-on-surface`, fixed so the label ignores dark mode. */
const MAP_LABEL_TEXT = '#1a1d1c';

export function pinLabelColors(
  withLabelBackground: boolean,
): { text: string; pillBorder: string; textHalo: string; pillFill: string } {
  return {
    // On a translucent black pill the text is white; without the pill it is the
    // light theme's on-surface colour, separated from the map by a white halo.
    text: withLabelBackground ? '#ffffff' : MAP_LABEL_TEXT,
    pillBorder: '#ffffff',
    pillFill: 'rgba(0,0,0,0.5)',
    textHalo: '#ffffff',
  };
}

// ── Pin name labels: when they show ───────────────────────────────────────

/**
 * Whether a pin's name label is drawn.
 *
 * The setting alone decides it. Deliberately NOT mobile's rule, which also gates
 * on zoom (`clampScaleFactor > minScaleFactorToShowIconLabels`, working out to
 * zoom > 13.5 — see `map_pins_layer.dart`). Mobile needs that gate because a phone
 * shows one map at one size; web renders the same map at three very different
 * sizes, and measured on a 1277×994 full-screen map at the zoom a hunting ground
 * opens at, none of nine labels collide — the room mobile does not have. A zoom
 * gate would also hide labels from a print of a whole ground, which is the case
 * the labels matter most for.
 *
 * So on web the switch in the view-settings panel is the whole control: on means
 * on, at every zoom. On the 460px-tall inline map that does mean labels can
 * overlap on a pin-dense ground; the switch is how you turn them off.
 */
export function shouldShowPinLabel({
  pinNamesVisible,
  label,
}: {
  pinNamesVisible: boolean
  label?: string
}): boolean {
  return pinNamesVisible && (label ?? '').length > 0
}
